import { lamportsToUiAmount, percentUiToDecimals } from "@bridgesplit/utils";
import { getZcCollateral, isManageLeverageEligable, useUnwindQuoteAndIxs } from "@bridgesplit/abf-react";
import { DEFAULT_SLIPPAGE } from "app/components/common";
import { WindType } from "@loopscale/pandora-types";

import { UseRepayWithCollateralQuoteParams, UseRepayWithCollateralQuoteReturn } from "./types";

export function useRepayWithCollateralQuote({
    loanExpanded,
    repayWithCollateralAmount,
    slippagePercentDecimals,
    skip
}: UseRepayWithCollateralQuoteParams): UseRepayWithCollateralQuoteReturn {
    const collateral = getZcCollateral(loanExpanded);
    const manageLeverageEligable = isManageLeverageEligable(loanExpanded);

    const skipCustomRepayWithCollateralQuote =
        !manageLeverageEligable || !repayWithCollateralAmount || skip || slippagePercentDecimals === undefined;

    // Custom repay quote - uses user input to determine amount of principal to repay
    const {
        data: repayWithCollateralQuote,
        isLoading: repayWithCollateralQuoteLoading,
        isFetching: repayWithCollateralQuoteIsFetching,
        error: repayWithCollateralQuoteError
    } = useUnwindQuoteAndIxs({
        loanExpanded,
        debtToRepay: 0, // just looking for the swap price here
        collateralAmount: repayWithCollateralAmount,
        loanCollateral: getZcCollateral(loanExpanded),
        slippagePercentDecimals: slippagePercentDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE),
        windType: WindType.Principal,
        skip: skipCustomRepayWithCollateralQuote,
        outputToken: loanExpanded?.principalMetadata
    });

    // Computed amounts
    const customRepayAmountInCollateral = lamportsToUiAmount(
        repayWithCollateralQuote?.quote.inputAmount ?? 0,
        collateral?.metadata.decimals
    );

    const customRepayAmountInPrincipalFromCollateralRepayment = lamportsToUiAmount(
        repayWithCollateralQuote?.quote.minOutputAmount ?? 0,
        loanExpanded?.principalMetadata.decimals
    );

    return {
        repayWithCollateralQuote,
        repayWithCollateralQuoteLoading: repayWithCollateralQuoteLoading,
        repayWithCollateralQuoteFetching: repayWithCollateralQuoteIsFetching,
        repayWithCollateralQuoteError:
            !repayWithCollateralQuoteLoading && !repayWithCollateralQuoteIsFetching
                ? repayWithCollateralQuoteError
                : undefined,
        customRepayAmountInCollateral,
        customRepayAmountInPrincipalFromCollateralRepayment,
        validHealthRepayQuote: true
    };
}
